import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import type { AxiosError } from 'axios'

import { Alert } from '@gto/ui/alert'
import { Button } from '@gto/ui/button'
import { Dialog } from '@gto/ui/dialog'

import { useAccountDestroy } from '@/api/gen'
import { useAuth } from '@/hooks/use-auth'
import { getAxiosErrorMessage } from '@/utils/helpers'

export const DeleteAccountButton = () => {
  const { onLogout } = useAuth()
  const { formatMessage } = useIntl()

  const [showConfirmation, setShowConfirmation] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onToggleModal = () => {
    setShowConfirmation((prev) => !prev)
  }

  const onConfirmDeletion = () => {
    setError(null)
    deleteAccount()
  }

  const { mutate: deleteAccount, isPending } = useAccountDestroy({
    mutation: {
      onSuccess: () => {
        onLogout()
        onToggleModal()
      },
      onError: (error: AxiosError) => {
        const err =
          getAxiosErrorMessage(error) || formatMessage({ id: 'user.settings.deleteAccount.error' })
        setError(err)
      },
    },
  })

  return (
    <>
      <Button
        fullWidth
        color="warning"
        size="large"
        variant="contained"
        onClick={onToggleModal}
      >
        <FormattedMessage id="common.controls.deleteAccount" />
      </Button>

      <Dialog.Root
        open={showConfirmation}
        onOpenChange={(o) => {
          if (!o) {
            onToggleModal()
          }
        }}
      >
        <Dialog.Portal>
          <Dialog.Backdrop />
          <Dialog.Popup className="w-full max-w-[600px]">
            <Dialog.Header>
              <FormattedMessage id="user.settings.deleteAccount.title" />
            </Dialog.Header>
            <Dialog.Content>
              <p className="text-warning-main">
                <FormattedMessage id="user.settings.deleteAccount.description" />
              </p>

              {error !== null && (
                <Alert
                  className="mt-4"
                  heading={error}
                  severity="warning"
                />
              )}
            </Dialog.Content>
            <Dialog.Footer>
              <Button
                color="secondary"
                disabled={isPending}
                size="large"
                variant="contained"
                onClick={onToggleModal}
              >
                <FormattedMessage id="common.controls.cancel" />
              </Button>
              <Button
                color="error"
                disabled={isPending}
                loading={isPending}
                size="large"
                variant="contained"
                onClick={onConfirmDeletion}
              >
                <FormattedMessage id="common.controls.deleteAccount" />
              </Button>
            </Dialog.Footer>
          </Dialog.Popup>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  )
}
